import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useProfile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bot, Plus, Calendar } from 'lucide-react';
import { useWorkoutPrograms } from '@/hooks/useWorkoutPrograms';
import { WorkoutProgramCard } from './WorkoutProgramCard';
import { WorkoutProgramViewer } from './WorkoutProgramViewer';
import { WorkoutProgram } from '@/hooks/useWorkoutPrograms';
import { CreateCustomProgramModal } from './CreateCustomProgramModal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const WorkoutProgramsSection = () => {
  const { profile } = useProfile();
  const coachName = profile?.ai_coach_name || 'Arnold';
  const { programs, loading, deleteProgram, updateProgramStatus, markDayComplete, refetch } = useWorkoutPrograms();
  const [selectedProgram, setSelectedProgram] = useState<WorkoutProgram | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();

  console.log('WorkoutProgramsSection - programs count:', programs.length, programs);


  const handleViewProgram = (program: WorkoutProgram) => {
    setSelectedProgram(program);
    setShowViewer(true);
  };

  const handleStatusUpdate = async (programId: string, status: string) => {
    try {
      await updateProgramStatus(programId, status);
      toast.success(`Program ${status} successfully!`);
    } catch (error) {
      toast.error('Failed to update program status');
    }
  };

  const handleDayComplete = async (programId: string, dayId: string, dayData: any) => {
    try {
      const workout = await markDayComplete(programId, dayId, dayData);
      return workout;
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteProgram = async (programId: string) => {
    try {
      await deleteProgram(programId);
      toast.success('Program deleted successfully');
    } catch (error) {
      toast.error('Failed to delete program');
    }
  };

  const handleCreateProgram = () => {
    setShowCreateModal(true);
  };

  const handleCreateWithArnold = () => {
    navigate("/chat", { 
      state: { 
        message: `Hey ${coachName}, I'd like a weekly workout program that fits my goals.`
      } 
    });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-normal">
            <Calendar className="h-5 w-5 text-primary" />
            Workout Programs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Loading workout programs...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 font-normal">
            <Calendar className="h-5 w-5 text-primary" />
            Workout Programs
          </CardTitle>
            <Button onClick={handleCreateProgram} size="sm" className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Create Custom Program
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {programs.length === 0 ? (
            <div className="text-center py-12 space-y-4">
              <Bot className="h-12 w-12 text-muted-foreground mx-auto" />
              <div>
                <p className="text-muted-foreground mb-2">
                  No workout programs created yet.
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  Chat with {coachName} to get personalized workout programs that provide structured training plans over weeks or months.
                </p>
                <Button onClick={handleCreateWithArnold} className="bg-gradient-primary text-black">
                  <Bot className="h-4 w-4 mr-2" />
                  Create with {coachName}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Arnold AI Programs Section */}
              {programs.some(p => p.created_from_chat) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Bot className="h-5 w-5 text-primary" />
                    AI Generated Programs
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      {programs.filter(p => p.created_from_chat).length}
                    </Badge>
                  </h3>
                  <div className="space-y-4">
                    {(showAllPrograms 
                      ? programs.filter(p => p.created_from_chat)
                      : programs.filter(p => p.created_from_chat).slice(0, 3)
                    ).map(program => (
                      <WorkoutProgramCard
                        key={program.id}
                        program={program}
                        onView={handleViewProgram}
                        onStatusUpdate={handleStatusUpdate}
                        onDelete={handleDeleteProgram}
                      />
                    ))}
                    {programs.filter(p => p.created_from_chat).length > 3 && (
                      <div className="pt-2 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAllPrograms(!showAllPrograms)}
                          className="w-full"
                        >
                          {showAllPrograms ? 'Show Less' : `Show All (${programs.filter(p => p.created_from_chat).length})`}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Active program highlight */}
              {programs.some(p => p.status === 'active' && !p.created_from_chat) && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                      Active Program
                    </Badge>
                  </h3>
                  <div className="space-y-4">
                    {programs
                      .filter(p => p.status === 'active' && !p.created_from_chat)
                      .map(program => (
                        <WorkoutProgramCard
                          key={program.id}
                          program={program}
                          onView={handleViewProgram}
                          onStatusUpdate={handleStatusUpdate}
                          onDelete={handleDeleteProgram}
                        />
                      ))
                    }
                  </div>
                </div>
              )}

              {/* Other programs (excluding active) */}
              {programs.some(p => !p.created_from_chat && p.status !== 'active') && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center justify-between">
                    Other Programs
                    <Badge variant="secondary">
                      {programs.filter(p => !p.created_from_chat && p.status !== 'active').length}
                    </Badge>
                  </h3>
                  
                  <div className="grid gap-4">
                    {(showAllPrograms 
                      ? programs.filter(p => !p.created_from_chat && p.status !== 'active')
                      : programs.filter(p => !p.created_from_chat && p.status !== 'active').slice(0, 3)
                    ).map(program => (
                      <WorkoutProgramCard
                        key={program.id}
                        program={program}
                        onView={handleViewProgram}
                        onStatusUpdate={handleStatusUpdate}
                        onDelete={handleDeleteProgram}
                      />
                    ))}
                    {programs.filter(p => !p.created_from_chat && p.status !== 'active').length > 3 && (
                      <div className="pt-2 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAllPrograms(!showAllPrograms)}
                          className="w-full"
                        >
                          {showAllPrograms ? 'Show Less' : `Show All (${programs.filter(p => !p.created_from_chat && p.status !== 'active').length})`}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <WorkoutProgramViewer
        program={selectedProgram}
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false);
          setSelectedProgram(null);
        }}
        onStatusUpdate={handleStatusUpdate}
        onDayComplete={handleDayComplete}
      />

      <CreateCustomProgramModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          refetch();
          toast.success('Program created! You can now activate it to start tracking.');
        }}
      />
    </>
  );
};